"use client";

import React from "react";
import EditSection, { TextEditor } from "../../sections/shared/EditSection";
import { SectionContentProps } from "../../types";
import { Input } from "rizzui";
import { PiTrash, PiUploadSimple } from "react-icons/pi";
import { getImageUrl } from "@/utils/image-url";
import homepageApi from "@/services/homepageApi";
import toast from "react-hot-toast";

export default function UniversityFooterEdit({
  content,
  onContentChange,
}: SectionContentProps) {
  const updateContent = (key: string, value: any) => {
    onContentChange?.({ ...content, [key]: value });
  };

  const socialLinks = content.socialLinks || [];

  return (
    <EditSection
      content={content}
      onContentChange={onContentChange || (() => {})}
      contentFields={
        <div className="space-y-4">
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Footer Text</h4>
            <div>
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-1 block">
                Footer Logo
              </label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-10 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 flex-shrink-0 relative group/img">
                  {content.logo ? (
                    <img
                      src={getImageUrl(content.logo) || content.logo}
                      alt="Logo Preview"
                      className="w-full h-full object-contain p-1"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs font-semibold">
                      LOGO
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover/img:opacity-100 transition-opacity cursor-pointer">
                    <PiUploadSimple className="w-4 h-4 text-white" />
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 2 * 1024 * 1024) {
                          toast.error("Image must be under 2MB");
                          return;
                        }
                        const uploadPromise = homepageApi.uploadImage(file);
                        toast.promise(uploadPromise, {
                          loading: "Uploading logo...",
                          success: "Logo uploaded!",
                          error: "Failed to upload logo",
                        });
                        const response = await uploadPromise;
                        if (response.success && response.data?.url) {
                          updateContent("logo", response.data.url);
                        }
                      }}
                    />
                  </label>
                </div>
                <Input
                  value={content.logo || ""}
                  onChange={(e) => updateContent("logo", e.target.value)}
                  placeholder="/uploads/logos/... or URL"
                  className="!rounded-lg text-xs flex-1 h-8"
                />
              </div>
            </div>
            <TextEditor
              label="Description"
              value={content.description}
              onChange={(v) => updateContent("description", v)}
              placeholder="About our university..."
              multiline
            />
            <TextEditor
              label="Address"
              value={content.address}
              onChange={(v) => updateContent("address", v)}
              placeholder="123 University Ave"
            />
            <TextEditor
              label="Phone"
              value={content.phone}
              onChange={(v) => updateContent("phone", v)}
              placeholder="Phone Number"
            />
            <TextEditor
              label="Email"
              value={content.email}
              onChange={(v) => updateContent("email", v)}
              placeholder="Email Address"
            />
            <TextEditor
              label="Copyright Brand Name"
              value={content.copyrightBrand}
              onChange={(v) => updateContent("copyrightBrand", v)}
              placeholder="Instacks"
            />
          </div>

          {/* Navigation Column 1 */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Column 1 Links</h4>
            <TextEditor
              label="Column 1 Title"
              value={content.col1Title || "Our Campus"}
              onChange={(v) => updateContent("col1Title", v)}
              placeholder="Our Campus"
            />
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide block">
                Links (One per line: Label | URL)
              </label>
              <textarea
                value={(content.col1Links || [
                  { label: "Academic", href: "#" },
                  { label: "Athletics", href: "#" },
                  { label: "Campus life", href: "#" },
                  { label: "Research", href: "#" },
                  { label: "Academic Area", href: "#" }
                ])
                  .map((l: any) => `${l.label}${l.href ? ` | ${l.href}` : ""}`)
                  .join("\n")}
                onChange={(e) => {
                  const lines = e.target.value.split("\n");
                  const newLinks = lines.map((line) => {
                    const [label, href] = line.split("|").map((s) => s.trim());
                    return { label: label || "", href: href || "#" };
                  });
                  updateContent("col1Links", newLinks);
                }}
                rows={5}
                className="w-full text-xs p-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Academic | /academic&#10;Athletics | /athletics"
              />
            </div>
          </div>

          {/* Navigation Column 2 */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Column 2 Links</h4>
            <TextEditor
              label="Column 2 Title"
              value={content.col2Title || "Our Campus"}
              onChange={(v) => updateContent("col2Title", v)}
              placeholder="Our Campus"
            />
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide block">
                Links (One per line: Label | URL)
              </label>
              <textarea
                value={(content.col2Links || [
                  { label: "About", href: "#" },
                  { label: "Tution Fee", href: "#" },
                  { label: "Alumni", href: "#" },
                  { label: "Faculty Staff", href: "#" },
                  { label: "Event", href: "#" }
                ])
                  .map((l: any) => `${l.label}${l.href ? ` | ${l.href}` : ""}`)
                  .join("\n")}
                onChange={(e) => {
                  const lines = e.target.value.split("\n");
                  const newLinks = lines.map((line) => {
                    const [label, href] = line.split("|").map((s) => s.trim());
                    return { label: label || "", href: href || "#" };
                  });
                  updateContent("col2Links", newLinks);
                }}
                rows={5}
                className="w-full text-xs p-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="About | /about&#10;Alumni | /alumni"
              />
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Quick Buttons (CTA)</h4>
            <TextEditor
              label="Section Title"
              value={content.ctaTitle || "Quick Button"}
              onChange={(v) => updateContent("ctaTitle", v)}
              placeholder="Quick Button"
            />
            <TextEditor
              label="Primary Button Text"
              value={content.primaryCtaText || "Applying"}
              onChange={(v) => updateContent("primaryCtaText", v)}
              placeholder="Applying"
            />
            <TextEditor
              label="Primary Button Link"
              value={content.primaryCtaLink || "#"}
              onChange={(v) => updateContent("primaryCtaLink", v)}
              placeholder="#"
            />
            <TextEditor
              label="Secondary Button Text"
              value={content.secondaryCtaText || "Scholarship"}
              onChange={(v) => updateContent("secondaryCtaText", v)}
              placeholder="Scholarship"
            />
            <TextEditor
              label="Secondary Button Link"
              value={content.secondaryCtaLink || "#"}
              onChange={(v) => updateContent("secondaryCtaLink", v)}
              placeholder="#"
            />
          </div>

          <div className="p-3 bg-gray-50 rounded-lg space-y-3">
            <h4 className="text-xs font-semibold text-gray-700">Social Links</h4>
            {[
              { key: "facebookUrl", label: "Facebook" },
              { key: "instagramUrl", label: "Instagram" },
              { key: "linkedinUrl", label: "LinkedIn" },
              { key: "youtubeUrl", label: "YouTube" },
            ].map((social) => (
              <div key={social.key} className="flex items-center gap-2">
                <span className="w-20 text-xs text-gray-600 font-medium">{social.label}</span>
                <Input
                  value={content[social.key] || ""}
                  onChange={(e) => updateContent(social.key, e.target.value)}
                  placeholder={`https://${social.label.toLowerCase()}.com/...`}
                  className="!rounded-lg flex-1 text-xs h-8"
                />
              </div>
            ))}
          </div>
        </div>
      }
    />
  );
}
